import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@/components/ui/carousel';
import { ChevronDown, Pin } from 'lucide-react';
import { formatCurrency, formatPoints } from '../utils/formatters';
import type { BuydownScenario } from './utils/buydownCalculations';
import { FilterChip } from './components/FilterChip';
import { ComparisonCard } from './components/ComparisonCard';

interface MobileRateSheetCardsProps {
  allScenarios: Array<{
    loanType: string;
    occupancy: string;
    label: string;
    basePoints: number;
    investorPoints: number;
    scenarios: {
      fixed?: BuydownScenario;
      oneZero?: BuydownScenario;
      twoOne?: BuydownScenario;
      threeTwoOne?: BuydownScenario;
    };
  }>;
}

const MobileRateSheetCards: React.FC<MobileRateSheetCardsProps> = ({ allScenarios }) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  
  // Comparison mode state
  const [pinnedScenario, setPinnedScenario] = useState<typeof allScenarios[0] | null>(null);
  
  // Filter state
  const [loanTypeFilter, setLoanTypeFilter] = useState<string>('all');
  const [occupancyFilter, setOccupancyFilter] = useState<string>('all');

  // Filter scenarios based on active filters
  const filteredScenarios = useMemo(() => {
    return allScenarios.filter(scenario => {
      const matchesLoanType = loanTypeFilter === 'all' || scenario.loanType === loanTypeFilter;
      const matchesOccupancy = occupancyFilter === 'all' || scenario.occupancy === occupancyFilter;
      return matchesLoanType && matchesOccupancy;
    });
  }, [allScenarios, loanTypeFilter, occupancyFilter]);

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  
  // Auto-scroll to first card when filters change
  useEffect(() => {
    if (api && filteredScenarios.length > 0) {
      api.scrollTo(0);
    }
  }, [loanTypeFilter, occupancyFilter, api, filteredScenarios]);
  
  // Clear pinned scenario if it's filtered out
  useEffect(() => {
    if (pinnedScenario && !filteredScenarios.find(s => 
      s.loanType === pinnedScenario.loanType && s.occupancy === pinnedScenario.occupancy
    )) {
      setPinnedScenario(null);
    }
  }, [filteredScenarios, pinnedScenario]);

  const getLoanTypeColor = (loanType: string) => {
    const colors = {
      conventional: 'blue',
      fha: 'green',
      va: 'purple',
      usda: 'orange'
    };
    return colors[loanType as keyof typeof colors] || 'gray';
  };

  const currentScenario = filteredScenarios[current - 1];
  
  const handlePinScenario = (scenario: typeof allScenarios[0]) => {
    if (pinnedScenario?.loanType === scenario.loanType && pinnedScenario?.occupancy === scenario.occupancy) {
      setPinnedScenario(null);
    } else {
      setPinnedScenario(scenario);
    }
  };

  const handleClearComparison = () => {
    setPinnedScenario(null);
  };

  return (
    <div className="space-y-4">
      {/* Filters Section */}
      <div className="space-y-3">
        {/* Loan Type Filters */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Loan Type</p>
          <div className="flex gap-2 overflow-x-auto pb-2 px-2 scrollbar-hide">
            <FilterChip 
              label="All" 
              count={allScenarios.length}
              active={loanTypeFilter === 'all'}
              onClick={() => setLoanTypeFilter('all')}
            />
            <FilterChip 
              label="Conventional" 
              icon="🔵"
              count={allScenarios.filter(s => s.loanType === 'conventional').length}
              active={loanTypeFilter === 'conventional'}
              onClick={() => setLoanTypeFilter('conventional')}
            />
            <FilterChip 
              label="FHA" 
              icon="🟢"
              count={allScenarios.filter(s => s.loanType === 'fha').length}
              active={loanTypeFilter === 'fha'}
              onClick={() => setLoanTypeFilter('fha')}
            />
            <FilterChip 
              label="VA" 
              icon="🟣"
              count={allScenarios.filter(s => s.loanType === 'va').length}
              active={loanTypeFilter === 'va'}
              onClick={() => setLoanTypeFilter('va')}
            />
            <FilterChip 
              label="USDA" 
              icon="🟠"
              count={allScenarios.filter(s => s.loanType === 'usda').length}
              active={loanTypeFilter === 'usda'}
              onClick={() => setLoanTypeFilter('usda')}
            />
          </div>
        </div>
        
        {/* Occupancy Filters */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Occupancy Type</p>
          <div className="flex gap-2 overflow-x-auto pb-2 px-2 scrollbar-hide">
            <FilterChip 
              label="All" 
              count={allScenarios.length}
              active={occupancyFilter === 'all'}
              onClick={() => setOccupancyFilter('all')}
            />
            <FilterChip 
              label="Primary" 
              icon="🏠"
              count={allScenarios.filter(s => s.occupancy === 'primary').length}
              active={occupancyFilter === 'primary'}
              onClick={() => setOccupancyFilter('primary')}
            />
            <FilterChip 
              label="Investment" 
              icon="💼"
              count={allScenarios.filter(s => s.occupancy === 'investment').length}
              active={occupancyFilter === 'investment'}
              onClick={() => setOccupancyFilter('investment')}
            />
            <FilterChip 
              label="2nd Home" 
              icon="🏖️"
              count={allScenarios.filter(s => s.occupancy === 'second').length}
              active={occupancyFilter === 'second'}
              onClick={() => setOccupancyFilter('second')}
            />
          </div>
        </div>
        
        {/* Active Filter Summary */}
        {(loanTypeFilter !== 'all' || occupancyFilter !== 'all') && (
          <div className="flex items-center justify-between text-xs px-2">
            <p className="text-muted-foreground">
              Showing {filteredScenarios.length} of {allScenarios.length} scenarios
            </p>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setLoanTypeFilter('all');
                setOccupancyFilter('all');
              }}
              className="h-6 text-xs"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Comparison Mode */}
      {pinnedScenario && currentScenario && (
        <ComparisonCard 
          pinnedScenario={pinnedScenario}
          currentScenario={currentScenario}
          onClearComparison={handleClearComparison}
        />
      )}

      {/* Scenario Counter */}
      <div className="flex items-center justify-between px-2">
        <p className="text-sm text-muted-foreground">
          {filteredScenarios.length === 0 ? 'No scenarios match filters' : `Comparing ${count} loan scenarios`}
        </p>
        {filteredScenarios.length > 0 && (
          <p className="text-sm font-medium">
            {current} of {count}
          </p>
        )}
      </div>

      {/* Carousel */}
      {filteredScenarios.length > 0 ? (
        <>
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: false,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {filteredScenarios.map((scenario) => (
                <CarouselItem key={`${scenario.loanType}-${scenario.occupancy}`} className="pl-4 basis-[90%]">
                  <LoanTypeCard 
                    scenario={scenario} 
                    color={getLoanTypeColor(scenario.loanType)}
                    onPin={handlePinScenario}
                    isPinned={pinnedScenario?.loanType === scenario.loanType && pinnedScenario?.occupancy === scenario.occupancy}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>

            <div className="flex justify-center gap-2 mt-4">
              <CarouselPrevious className="relative left-0 translate-y-0" />
              <CarouselNext className="relative right-0 translate-y-0" />
            </div>
          </Carousel>

          <div className="flex justify-center gap-2 py-2">
            {Array.from({ length: count }).map((_, index) => (
              <button
                key={index}
                onClick={() => api?.scrollTo(index)}
                className={`h-2 rounded-full transition-all ${
                  index === current - 1
                    ? 'w-6 bg-primary'
                    : 'w-2 bg-muted-foreground/30'
                }`}
                aria-label={`Go to card ${index + 1}`}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No loan scenarios match your filters.</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              setLoanTypeFilter('all');
              setOccupancyFilter('all');
            }}
            className="mt-3"
          >
            Reset Filters
          </Button>
        </div>
      )}

      <div className="mt-4 p-3 bg-muted/30 rounded-lg text-xs space-y-2">
        <p className="font-semibold">Notes:</p>
        <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
          <li>All scenarios based on 4.99% fixed rate for 30 years</li>
          <li>Conventional investor/2nd home pricing includes +2.125 points</li>
          <li>Builder credit percentages vary by buydown type</li>
          <li>Monthly payments include PITI (principal, interest, taxes, insurance)</li>
        </ul>
      </div>
    </div>
  );
};

interface LoanTypeCardProps {
  scenario: {
    loanType: string;
    occupancy: string;
    label: string;
    basePoints: number;
    investorPoints: number;
    scenarios: {
      fixed?: BuydownScenario;
      oneZero?: BuydownScenario;
      twoOne?: BuydownScenario;
      threeTwoOne?: BuydownScenario;
    };
  };
  color: string;
  onPin: (scenario: any) => void;
  isPinned: boolean;
}

const LoanTypeCard: React.FC<LoanTypeCardProps> = ({ scenario, color, onPin, isPinned }) => {
  return (
    <Card className={`h-full ${isPinned ? 'ring-2 ring-primary' : ''}`}>
      <CardHeader className={`bg-${color}-50 dark:bg-${color}-950 pb-3`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-bold text-lg">{scenario.label}</h3>
            <p className="text-sm text-muted-foreground">
              Base: {scenario.basePoints.toFixed(3)} points
            </p>
          </div>
          <div className="flex items-center gap-2">
            {scenario.investorPoints > 0 && (
              <Badge variant="outline" className="bg-amber-100 dark:bg-amber-900">
                +{scenario.investorPoints.toFixed(3)} inv
              </Badge>
            )}
            <Button 
              variant={isPinned ? "default" : "ghost"}
              size="sm" 
              onClick={() => onPin(scenario)}
              className="h-8 w-8 p-0"
            >
              <Pin className={`w-4 h-4 ${isPinned ? 'fill-current' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-3">
        <BuydownOptionRow
          title="4.99% Fixed Rate"
          subtitle="30 years"
          scenario={scenario.scenarios.fixed}
          color="blue"
          isDefault
        />
        <Separator />
        <BuydownOptionRow
          title="1/0 Buydown"
          subtitle="3.99% → 4.99%"
          scenario={scenario.scenarios.oneZero}
          color="green"
        />
        <Separator />
        <BuydownOptionRow
          title="2/1 Buydown"
          subtitle="2.99% → 3.99% → 4.99%"
          scenario={scenario.scenarios.twoOne}
          color="amber"
        />
        <Separator />
        <BuydownOptionRow
          title="3/2/1 Buydown"
          subtitle="1.99% → 2.99% → 3.99% → 4.99%"
          scenario={scenario.scenarios.threeTwoOne}
          color="purple"
        />
      </CardContent>
    </Card>
  );
};

interface BuydownOptionRowProps {
  title: string;
  subtitle: string;
  scenario?: BuydownScenario;
  color: string;
  isDefault?: boolean;
}

const BuydownOptionRow: React.FC<BuydownOptionRowProps> = ({
  title,
  subtitle,
  scenario,
  color,
  isDefault
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!scenario) return null;

  const hasMultiplePeriods = scenario.paymentSchedule.length > 1;
  
  // Calculate savings compared to first year payment (baseline)
  const baselinePayment = scenario.paymentSchedule[0]?.monthlyPayment || 0;
  
  const calculateTotalSavings = () => {
    if (!hasMultiplePeriods) return 0;
    let totalSavings = 0;
    
    scenario.paymentSchedule.forEach((period, idx) => {
      if (idx === 0) return; // Skip first period (baseline)
      const monthsInPeriod = getMonthsInPeriod(period.period);
      const savingsPerMonth = baselinePayment - period.monthlyPayment;
      if (savingsPerMonth > 0) {
        totalSavings += savingsPerMonth * monthsInPeriod;
      }
    });
    
    return totalSavings;
  };

  const getMonthsInPeriod = (period: string): number => {
    if (period.includes('Year 1')) return 12;
    if (period.includes('Year 2')) return 12;
    if (period.includes('Year 3')) return 12;
    if (period.includes('Years')) {
      const match = period.match(/Years? (\d+)-(\d+)/);
      if (match) {
        const start = parseInt(match[1]);
        const end = parseInt(match[2]);
        return (end - start + 1) * 12;
      }
    }
    return 12;
  };

  const getMonthRange = (periodIndex: number): string => {
    if (periodIndex === 0) return 'Months 1-12';
    if (periodIndex === 1) return scenario.paymentSchedule.length === 2 ? 'Months 13-360' : 'Months 13-24';
    if (periodIndex === 2) return scenario.paymentSchedule.length === 3 ? 'Months 25-360' : 'Months 25-36';
    return 'Months 37-360';
  };

  return (
    <div className={`rounded-lg bg-${color}-50/50 dark:bg-${color}-950/30`}>
      <button 
        onClick={() => hasMultiplePeriods && setIsExpanded(!isExpanded)}
        className={`w-full p-3 text-left ${hasMultiplePeriods ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-sm">{title}</h4>
              {hasMultiplePeriods && (
                <ChevronDown 
                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
          {isDefault && (
            <Badge variant="secondary" className="text-xs">Default</Badge>
          )}
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Points:</span>
            <span className="font-mono font-semibold">{formatPoints(scenario.points)}</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Year 1 Payment:</span>
            <span className="font-bold text-lg">
              {formatCurrency(scenario.paymentSchedule[0]?.monthlyPayment || 0)}
            </span>
          </div>

          {scenario.builderCredit > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Builder Credit:</span>
              <Badge variant="outline" className="bg-green-100 dark:bg-green-900 font-semibold">
                {formatCurrency(scenario.builderCredit)}
              </Badge>
            </div>
          )}

          <div className="space-y-1.5 pt-2 border-t">
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">Before Credit:</span>
              <span className="text-muted-foreground font-mono">
                {formatCurrency(scenario.cashToCloseBeforeCredit)}
              </span>
            </div>
            {scenario.builderCredit > 0 && (
              <div className="flex justify-between items-center text-xs text-green-600 dark:text-green-400">
                <span>Credit Applied:</span>
                <span className="font-mono">-{formatCurrency(scenario.builderCredit)}</span>
              </div>
            )}
            {scenario.earnestMoneyDeposit > 0 && (
              <div className="flex justify-between items-center text-xs text-green-600 dark:text-green-400">
                <span>Earnest Money:</span>
                <span className="font-mono">-{formatCurrency(scenario.earnestMoneyDeposit)}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground font-medium">Cash to Close:</span>
              <span className="font-bold">
                {formatCurrency(scenario.cashToClose)}
              </span>
            </div>
          </div>
        </div>
      </button>
      
      {/* Expanded payment schedule */}
      {isExpanded && hasMultiplePeriods && (
        <div className="px-3 pb-3 pt-2 space-y-3 border-t">
          <p className="text-xs font-semibold text-muted-foreground">Payment Schedule Breakdown</p>
          
          {scenario.paymentSchedule.map((period, idx) => {
            const monthsInPeriod = getMonthsInPeriod(period.period);
            const totalForPeriod = period.monthlyPayment * monthsInPeriod;
            const isIncreased = idx > 0;
            
            return (
              <div 
                key={idx}
                className={`
                  p-3 rounded-md space-y-2
                  ${idx === 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted/50'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold">{period.period}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {getMonthRange(idx)}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {period.rate.toFixed(2)}%
                    {idx === 0 && scenario.paymentSchedule.length > 1 && (
                      <span className="ml-1 text-green-600">Lowest</span>
                    )}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Monthly</p>
                    <p className="font-bold">{formatCurrency(period.monthlyPayment)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Period Total</p>
                    <p className="font-bold">{formatCurrency(totalForPeriod)}</p>
                  </div>
                </div>
                
                {isIncreased && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        Monthly increase
                      </span>
                      <span className="font-semibold text-amber-600 dark:text-amber-400">
                        +{formatCurrency(period.monthlyPayment - scenario.paymentSchedule[idx - 1].monthlyPayment)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Total savings summary */}
          {calculateTotalSavings() > 0 && (
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold">Buydown Period Savings:</span>
                <span className="font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(calculateTotalSavings())}
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                * Savings from lower initial rates during buydown period
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileRateSheetCards;
